import { useMemo } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { isGolfOSVariant } from '../config/branding';

/**
 * Golf/Club Management terminology preset
 * This can be reused for multiple golf-related tenants
 */
const GOLF_CLUB_TERMINOLOGY: Record<string, string> = {
  // ============================================
  // MENU TERMINOLOGY
  // ============================================
  // Main CRM section -> Club Management
  'menu.crm': 'Club Management',
  
  // CRM menu items
  'menu.customers': 'Members / Residents',
  'menu.leads': 'Leads & Waitlist',
  'menu.opportunities': 'Contracts & Renewals',
  
  // Sales/Financials menu section
  'menu.sales': 'Financials & Billing',
  
  // Sales menu items
  'menu.estimates': 'Estimates & Quotes',
  'menu.invoices': 'Invoicing & Dues',
  'menu.payments': 'Payments Received',
  
  // Golf & Amenity Booking menu section
  'menu.golfBooking': 'GOLF & AMENITY BOOKING',
  'menu.teeTimeManagement': 'Tee Time Management',
  'menu.courseServices': 'Course Services',
  'menu.facilityReservations': 'Facility Reservations',
  
  // Operations menu section
  'menu.operations': 'Service & Field Dispatch',
  
  // Assets menu section
  'menu.assets': 'Course & Community Assets',
  
  // Assets menu items
  'menu.sites': 'Property Sites / Zones',
  'menu.vehicles': 'Maintenance Fleet & Equipment',
  'menu.drivers': 'Residential Units',
  
  // Other menu sections
  'menu.tasks': 'Service Requests',
  'menu.schedule': 'Schedule',
  'menu.completedForms': 'Completed Forms',
  'menu.chat': 'Chat',
  'menu.memberMessaging': 'Member/Resident Messaging',
  
  // ============================================
  // DASHBOARD TERMINOLOGY
  // ============================================
  // Section titles
  'dashboard.crm.title': 'Club Management',
  'dashboard.sales.title': 'Financials & Billing',
  'dashboard.operations.title': 'Service & Field Dispatch',
  'dashboard.assets.title': 'Course & Community Assets',
  
  // Metric cards
  'dashboard.customers': 'Members / Residents',
  'dashboard.leads': 'Leads & Waitlist',
  'dashboard.opportunities': 'Contracts & Renewals',
  'dashboard.recentLeads': 'Recent Leads & Waitlist',
  'dashboard.drivers': 'Staff',
  'dashboard.vehicles': 'Golf Carts',
  'dashboard.tasks': 'Service Requests',
  'dashboard.totalTasks': 'Total Service Requests',
  'dashboard.recentTasks': 'Recent Service Requests',
  
  // Quick actions
  'dashboard.addCustomer': 'Add Member',
  'dashboard.addLead': 'Add Prospect',
  'dashboard.addTask': 'Add Service Request',
  
  // Descriptions
  'dashboard.manageFleet': 'Manage your golf carts and staff',
  /** GolfOS-only home dashboard */
  'dashboard.golfos.subtitle': 'Service requests and field dispatch',

  // ============================================
  // PAGE TITLES
  // ============================================
  // List pages
  'pages.customers': 'Members / Residents',
  'pages.leads': 'Leads & Waitlist',
  'pages.opportunities': 'Contracts & Renewals',
  'pages.tasks': 'Service Requests',
  'pages.vehicles': 'Maintenance Fleet & Equipment',
  'pages.drivers': 'Residential Units',
  'pages.sites': 'Property Sites / Zones',
  'pages.invoices': 'Invoicing & Dues',
  'pages.estimates': 'Estimates & Quotes',
  'pages.payments': 'Payments Received',
  'pages.routes': 'Routes',
  'pages.chat': 'Chat',
  'pages.users': 'Users',
  'pages.workflows': 'Workflows',
  'pages.equipment': 'Maintenance Fleet & Equipment',
  'pages.notifications': 'Notifications',
  'pages.enterprises': 'Enterprises',
  'pages.brands': 'Brands',
  'pages.regions': 'Regions',
  
  // Create/Edit pages
  'pages.createCustomer': 'Add Member / Resident',
  'pages.createLead': 'Add Lead / Waitlist',
  'pages.createOpportunity': 'Create Contract & Renewal',
  'pages.createTask': 'Create Service Request',
  'pages.editTask': 'Edit Service Request',
  'pages.createVehicle': 'Add Fleet & Equipment',
  'pages.editVehicle': 'Edit Fleet & Equipment',
  'pages.createDriver': 'Add Residential Unit',
  'pages.editDriver': 'Edit Residential Unit',
  'pages.createSite': 'Add Property Site / Zone',
  'pages.editSite': 'Edit Property Site / Zone',
  
  // Page subtitles
  'pages.customers.subtitle': 'Manage your members and residents database',
  'pages.leads.subtitle': 'Leads and waitlist entries',
  'pages.vehicles.subtitle': 'Manage your maintenance fleet and equipment',
  'pages.drivers.subtitle': 'Manage residential units, homes, and condos',
};

const TERMINOLOGY_PRESETS: Record<string, Record<string, string>> = {
  // ============================================
  // DEMO CONFIGURATION - Add your tenant codes here
  // ============================================
  // 
  // To enable Club Management terminology for a tenant:
  // 1. Find your tenant code (check the tenant selector in the UI or database)
  // 2. Add it below as a key (lowercase) and assign GOLF_CLUB_TERMINOLOGY
  // 
  // Example tenant codes to try (uncomment and modify):
  // 'golf-club-1': GOLF_CLUB_TERMINOLOGY,
  // 'golf-club': GOLF_CLUB_TERMINOLOGY,
  // 'golf': GOLF_CLUB_TERMINOLOGY,
  // 'club-1': GOLF_CLUB_TERMINOLOGY,
  // 'demo-golf': GOLF_CLUB_TERMINOLOGY,
  
  // Golf tenants
  'dg': GOLF_CLUB_TERMINOLOGY,
  'magna': GOLF_CLUB_TERMINOLOGY,
  
  // Or create custom terminology for a specific tenant:
  // 'your-tenant-code': {
  //   'menu.crm': 'Club Management',
  //   'menu.customers': 'Members',
  //   // ... add more customizations
  // },
  
  // Default: empty object means use standard terminology
  default: {},
};

/**
 * Hook to get tenant-specific terminology
 * Returns a function that can translate menu keys to tenant-specific strings
 */
export const useTenantTerminology = () => {
  const { currentTenant } = useTenant();

  const terminology = useMemo(() => {
    // GolfOS variant always uses golf terminology regardless of tenant code
    if (isGolfOSVariant) {
      return GOLF_CLUB_TERMINOLOGY;
    }

    if (!currentTenant?.code) {
      return TERMINOLOGY_PRESETS.default || {};
    }

    const tenantCode = currentTenant.code.toLowerCase();

    // First, check for exact tenant code match
    if (TERMINOLOGY_PRESETS[tenantCode]) {
      return TERMINOLOGY_PRESETS[tenantCode];
    }

    // Pattern matching: apply golf/club terminology if tenant code contains these keywords
    if (tenantCode.includes('golf') || tenantCode.includes('club')) {
      return GOLF_CLUB_TERMINOLOGY;
    }

    // Fallback to default
    return TERMINOLOGY_PRESETS.default || {};
  }, [currentTenant?.code]);

  /**
   * Get tenant-specific string for a key, or return default
   */
  const t = (key: string, defaultValue: string): string => {
    return terminology[key] || defaultValue;
  };

  return { terminology, t };
};

